import { CaretLeft, Ticket, Gift, CheckCircle } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { HashLoader } from "react-spinners";
import {
  useReferralRewards,
  ReferralReward,
} from "../../../hooks/useReferralRewards";
import { useClaimReferralReward } from "../../../hooks/useClaimReferralReward";

const naira = (n: number | null) =>
  n == null ? "" : `₦${Number(n).toLocaleString()}`;

const ReferralRewards = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useReferralRewards({
    status: "CLAIMABLE",
    take: 50,
  });
  const claim = useClaimReferralReward();

  const items = data?.data?.items ?? [];

  const onClaim = (reward: ReferralReward) => {
    claim.mutate(reward.id, {
      onSuccess: (res) => {
        toast.success(
          res?.message ?? "Reward claimed — it's on the way to your phone."
        );
        refetch();
      },
      onError: (e) => {
        toast.error((e as Error)?.message ?? "Could not claim reward.");
      },
    });
  };

  return (
    <div className="relative min-h-[70vh] px-5 py-8">
      <div className="mx-auto max-w-2xl flex flex-col gap-6">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="p-2.5 rounded-full border border-gray-300 text-deepBlue hover:bg-gray-50 transition shrink-0"
            aria-label="Go back"
          >
            <CaretLeft weight="bold" />
          </button>
          <h1 className="font-bold text-xl sm:text-2xl text-gray-900">
            Referral rewards
          </h1>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 rounded-xl bg-orange-50 text-visaro-orange">
              <Gift size={24} weight="duotone" />
            </div>
            <div>
              <h3 className="font-semibold text-deepBlue text-lg">
                Claim your gifts
              </h3>
              <p className="text-gray-600 text-sm mt-0.5">
                Data and airtime rewards you've earned are delivered straight to
                your registered phone number.
              </p>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center gap-3 py-8">
              <HashLoader size={24} color="#EA5C29" />
              <span className="text-gray-600 text-sm">Loading rewards…</span>
            </div>
          ) : isError ? (
            <div className="bg-amber-50 rounded-xl border border-amber-100 p-4 text-sm text-amber-900">
              <p className="font-medium">Could not load your rewards</p>
              <p className="text-amber-800/90 mt-1">
                {(error as Error)?.message ?? "Try again later."}
              </p>
              <button
                type="button"
                onClick={() => refetch()}
                className="mt-2 text-visaro-orange font-semibold hover:underline"
              >
                Retry
              </button>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle
                size={40}
                weight="duotone"
                className="mx-auto text-emerald-500 mb-2"
              />
              <p className="text-gray-600 text-sm">
                No rewards to claim right now. Invite friends to earn data and
                airtime gifts.
              </p>
              <button
                type="button"
                onClick={() => navigate("/referrals")}
                className="mt-3 text-visaro-orange font-semibold hover:underline text-sm"
              >
                Go to referrals
              </button>
            </div>
          ) : (
            <ul className="flex flex-col divide-y divide-gray-100">
              {items.map((r) => (
                <li
                  key={r.id}
                  className="py-4 flex items-center justify-between gap-4"
                >
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="p-2 rounded-xl bg-orange-50 text-visaro-orange shrink-0">
                      <Ticket size={20} weight="duotone" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-deepBlue text-sm">
                        {r.rewardMeta?.voucherKind === "AIRTIME"
                          ? "Airtime"
                          : "Data"}{" "}
                        reward
                        {r.rewardMeta?.planName ? ` · ${r.rewardMeta.planName}` : ""}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {naira(r.amount)}
                        {r.rewardMeta?.network
                          ? ` · ${String(r.rewardMeta.network).toUpperCase()}`
                          : ""}
                        {r.voucherCode ? ` · ${r.voucherCode}` : ""}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={claim.isLoading}
                    onClick={() => onClaim(r)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-visaro-orange text-white text-sm font-semibold hover:bg-visaro-orange/90 transition-colors disabled:opacity-60"
                  >
                    {claim.isLoading && claim.variables === r.id
                      ? "Claiming…"
                      : "Claim"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="text-xs text-gray-400 text-center">
          Rewards are delivered to your registered phone number and are subject to
          Visaro's referral terms.
        </p>
      </div>
    </div>
  );
};

export default ReferralRewards;
